import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client/react";

import SearchInput from "components/SearchInput";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import useTableSort from "components/Table/useTableSort";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored, InfoCircle } from "components/icons/generated";
import useSelectionStore from "components/Table/useSelectionStore";
import useTypedContext from "hooks/useTypedContext";
import ButtonIcon from "ds/components/ButtonIcon";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import { showManageRolesDrawer } from "../ManageRolesDrawer";
import { SettingsContext } from "../../Context";
import {
  API_KEY_ROLES_COLUMN_CONFIG,
  API_KEY_ROLES_CUSTOM_END_COLUMN_CONFIG,
  INITIAL_COLUMNS_VIEW_CONFIG,
  ROW_HEADER_ID,
  FILTERS_ORDER_SETTINGS_KEY,
  API_KEY_ROLES_COLUMNS_STORAGE_KEY,
  SORT_CONFIG,
  initialSortOption,
  initialSortDirection,
} from "./constants";
import ApiKeyRolesEmpty from "./Empty";
import ApiKeyRolesListItem from "./ListItem";
import { useFilteredEntities } from "./useFilteredEntities";
import { ApiKeyView, ApiKeyViewRoleBinding } from "../types";
import ApiKeyRolesBulkActions from "./BulkActions";
import { GetApiKeyData } from "../gql";
import { showRolesInfoDrawer } from "../../Roles/InfoDrawer";

type ApiKeyRolesProps = {
  apiKey: ApiKeyView;
  refetch: useQuery.Result<GetApiKeyData>["refetch"];
};

const ApiKeyRoles = ({ apiKey, refetch }: ApiKeyRolesProps) => {
  const { resetAllSelected, selected, unselect, resetStore } = useSelectionStore();
  const { activationStatus } = useTypedContext(SettingsContext);

  // reset store when unmounted
  useEffect(() => resetStore, [resetStore]);

  const roleBindings = apiKey.apiKeyRoleBindings;

  const virtualizedListContainerRef = useRef<HTMLDivElement>(null);

  const { sortDescriptor, handleSortOptionChange } = useTableSort({
    initialSortOption,
    initialSortDirection,
    sortConfig: SORT_CONFIG,
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
  });

  const filteredRoles = useFilteredEntities(roleBindings, sortDescriptor);
  const isPageEmpty = filteredRoles.length === 0;

  const handleBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const onBulkResetAll = useCallback(() => {
    resetAllSelected();
  }, [resetAllSelected]);

  const handleManageRolesClick = useCallback(() => {
    showManageRolesDrawer({
      apiKeyId: apiKey.id,
      activationStatus,
    });
  }, [apiKey.id, activationStatus]);

  const openInfoDrawer = useCallback(() => {
    showRolesInfoDrawer();
  }, []);

  const memoizedSelectedApiKeyRolesMap = useMemo(
    () => new Map(roleBindings.map((role) => [role.id, role])),
    [roleBindings]
  );

  const hasOnlyOneRole = useMemo(() => {
    return roleBindings.length === 1;
  }, [roleBindings]);

  const renderRow = useCallback(
    (
      item: Partial<ApiKeyViewRoleBinding> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <ApiKeyRolesListItem
            roleBinding={item as ApiKeyViewRoleBinding}
            isLastRole={hasOnlyOneRole}
          />
        </TableRow>
      );
    },
    [hasOnlyOneRole]
  );

  return (
    <>
      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" gap="medium">
          <PageInfoHeaderTitle title="Roles" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
        </Box>
        <PageInfoHeaderActions>
          {!!roleBindings.length && (
            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.ROLES}
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
          )}
          <Button variant="primary" onPress={handleManageRolesClick}>
            Manage roles
          </Button>
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <TableContextProvider
        selectable
        columnsConfig={API_KEY_ROLES_COLUMN_CONFIG}
        endCustomColumns={API_KEY_ROLES_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={API_KEY_ROLES_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
      >
        <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
          {!!filteredRoles.length && (
            <Table ariaLabel="Roles" items={filteredRoles} ref={virtualizedListContainerRef}>
              {renderRow}
            </Table>
          )}

          {selected.size > 0 && (
            <ApiKeyRolesBulkActions
              virtualizedListContainerRef={virtualizedListContainerRef}
              selectedSet={selected}
              apiKeyRolesMap={memoizedSelectedApiKeyRolesMap}
              onBulkResetAll={onBulkResetAll}
              onItemDismiss={unselect}
              onFinish={handleBulkActionsFinish}
            />
          )}
          {!roleBindings.length && <ApiKeyRolesEmpty />}
          {isPageEmpty && !!roleBindings.length && (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          )}
        </TableWrapper>
      </TableContextProvider>
    </>
  );
};

export default ApiKeyRoles;
